import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpenIcon, BookmarkIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import WebAccessModal from './WebAccessModal';
import HardCopyModal from './HardCopyModal';

const AccessOptions = ({ isOpen, onClose, onAccessGranted }) => {
  const { user, hasPurchased } = useAuth();
  const navigate = useNavigate();
  const [showWebAccess, setShowWebAccess] = useState(false);
  const [showHardCopy, setShowHardCopy] = useState(false);

  const handleReadNow = () => {
    onClose();
    navigate('/reader-pageflip');
  };

  const handleWebAccess = () => {
    if (!user) {
      alert('Please login first to access the book');
      onClose();
      return;
    }
    setShowWebAccess(true);
  };

  const handleHardCopy = () => {
    if (!user) {
      alert('Please login first to place an order');
      onClose();
      return;
    }
    setShowHardCopy(true);
  };

  const handleWebAccessGranted = () => {
    setShowWebAccess(false);
    onClose();
    if (onAccessGranted) onAccessGranted();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal — slides up on mobile, scales in on desktop */}
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 60, scale: 0.97 }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          className="relative w-full sm:max-w-2xl bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden"
          style={{ maxHeight: '92vh', overflowY: 'auto' }}
        >
          {/* Header */}
          <div
            className="relative px-6 pt-7 pb-6 text-white"
            style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors focus:outline-none"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5 text-white" />
            </button>

            {/* Drag handle (mobile cue) */}
            <div className="w-10 h-1 bg-white/30 rounded-full mx-auto mb-5 sm:hidden" />

            <div className="flex items-center gap-2 justify-center mb-1">
              <SparklesIcon className="h-5 w-5 text-yellow-300" />
              <h2 className="text-2xl font-bold tracking-tight">Access Fijoli</h2>
            </div>
            <p className="text-center text-sm text-red-100 leading-relaxed">
              Choose how you'd like to read the book
            </p>

            {user && (
              <div className="mt-4 flex items-center justify-center gap-2 bg-white/10 rounded-xl px-4 py-2.5">
                <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                  {(user.name || user.phone || '?')[0].toUpperCase()}
                </div>
                <span className="text-sm text-white/90">
                  {user.name || user.phone}
                </span>
                {hasPurchased && (
                  <span className="ml-auto flex items-center gap-1 text-xs text-green-300 font-medium">
                    <CheckIcon className="h-3.5 w-3.5" /> Active
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Body */}
          <div className="px-5 py-5">
            {/* ── Read Now banner for paid users ── */}
            {user && hasPurchased && (
              <div className="mb-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-4 flex items-center gap-3 shadow-md">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <BookOpenIcon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white">You have full access 🎉</p>
                  <p className="text-xs text-blue-200">Jump straight into the book</p>
                </div>
                <button
                  onClick={handleReadNow}
                  className="shrink-0 bg-white text-blue-600 font-bold text-sm px-4 py-2 rounded-xl hover:bg-blue-50 active:scale-95 transition-all shadow-sm"
                >
                  Read Now →
                </button>
              </div>
            )}

            {!user ? (
              /* Not logged in */
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Login Required</h3>
                <p className="text-gray-500 text-sm mb-6 max-w-xs mx-auto">
                  Create a free account or sign in to get access to Fijoli.
                </p>
                <button
                  onClick={onClose}
                  className="w-full sm:w-auto px-8 py-3 bg-red-600 text-white rounded-2xl text-sm font-semibold hover:bg-red-700 transition-colors shadow-md"
                >
                  Login / Sign Up
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Web Access Card */}
                {!hasPurchased && (
                  <motion.button
                    whileTap={{ scale: 0.985 }}
                    onClick={handleWebAccess}
                    className="w-full text-left rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-200 transition-all duration-200 p-4 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm">
                        <BookOpenIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="font-bold text-gray-900 text-base">Web Access</span>
                          <span className="text-xs font-semibold text-orange-600 bg-orange-100 border border-orange-200 px-2 py-0.5 rounded-full">
                            Early Bird 🎉
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1.5 mb-2">
                          <span className="text-2xl font-bold text-blue-600">₹99</span>
                          <span className="text-sm line-through text-gray-400">₹299</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">Read online anytime, any device</p>
                        <ul className="space-y-1.5">
                          {['Interactive 3D page turning', 'Search by keywords', 'New topics updated regularly', 'WhatsApp notifications'].map(f => (
                            <li key={f} className="flex items-center gap-1.5 text-sm text-gray-600">
                              <CheckIcon className="h-4 w-4 text-green-500 shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="shrink-0 self-center">
                        <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </motion.button>
                )}

                {/* Hard Copy Card */}
                {!hasPurchased && (
                  <motion.button
                    whileTap={{ scale: 0.985 }}
                    onClick={handleHardCopy}
                    className="w-full text-left rounded-2xl border border-green-100 bg-gradient-to-br from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 hover:border-green-200 transition-all duration-200 p-4 focus:outline-none focus:ring-2 focus:ring-green-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-12 h-12 rounded-xl bg-green-600 flex items-center justify-center shadow-sm">
                        <BookmarkIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-bold text-gray-900 text-base">Hard Copy</span>
                        </div>
                        <div className="flex items-baseline gap-1.5 mb-2">
                          <span className="text-2xl font-bold text-green-600">₹1499</span>
                          <span className="text-xs text-gray-400">incl. shipping</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">Physical book at your doorstep</p>
                        <ul className="space-y-1.5">
                          {['Physical book delivery', 'Premium quality printing', 'Free shipping', 'Track your order'].map(f => (
                            <li key={f} className="flex items-center gap-1.5 text-sm text-gray-600">
                              <CheckIcon className="h-4 w-4 text-green-500 shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="shrink-0 self-center">
                        <svg className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </motion.button>
                )}

                {/* Upgrade card */}
                {hasPurchased && !user.hasHardCopy && !user.hasUpgraded && (
                  <motion.button
                    whileTap={{ scale: 0.985 }}
                    onClick={handleHardCopy}
                    className="w-full text-left rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 hover:border-amber-200 transition-all duration-200 p-4 focus:outline-none focus:ring-2 focus:ring-amber-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center text-2xl shadow-sm">
                        📖
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-bold text-gray-900 text-base block mb-0.5">Upgrade to Hard Copy</span>
                        <div className="flex items-baseline gap-1.5 mb-2">
                          <span className="text-2xl font-bold text-amber-600">₹1499</span>
                        </div>
                        <ul className="space-y-1.5">
                          {['Keep your online access', 'Physical book delivery', 'Premium quality printing', 'Free shipping included'].map(f => (
                            <li key={f} className="flex items-center gap-1.5 text-sm text-gray-600">
                              <CheckIcon className="h-4 w-4 text-green-500 shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="shrink-0 self-center">
                        <svg className="h-5 w-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </motion.button>
                )}

                {/* Already upgraded */}
                {hasPurchased && (user.hasHardCopy || user.hasUpgraded) && (
                  <div className="rounded-2xl border border-green-200 bg-green-50 p-5 text-center">
                    <div className="text-3xl mb-2">🎉</div>
                    <h3 className="font-bold text-green-800 mb-1">All Set!</h3>
                    <p className="text-sm text-green-600">You have both online and physical book access.</p>
                    <div className="mt-3 flex justify-center gap-3 text-xs text-green-700">
                      <span className="flex items-center gap-1"><CheckIcon className="h-3.5 w-3.5" /> Online access</span>
                      <span className="flex items-center gap-1"><CheckIcon className="h-3.5 w-3.5" /> Hard copy</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Footer note */}
            <p className="mt-4 text-center text-xs text-gray-400">
              🔒 Secure payments via Razorpay
            </p>
          </div>
        </motion.div>
      </div>

      {/* Child Modals */}
      <AnimatePresence>
        {showWebAccess && (
          <WebAccessModal
            isOpen={showWebAccess}
            onClose={() => setShowWebAccess(false)}
            onAccessGranted={handleWebAccessGranted}
          />
        )}
        {showHardCopy && (
          <HardCopyModal
            isOpen={showHardCopy}
            onClose={() => setShowHardCopy(false)}
          />
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
};

export default AccessOptions;
